import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { SatProductSearch } from "../../components/ui/sat-search";
import { Loader2, Plus, Trash2, Calculator, MapPin, Package, Info, AlertTriangle, CheckCircle, Fuel, Wine, ArrowDownLeft, Building2, User, Smartphone, Gamepad2, Trophy, Car, ShoppingBag, Home, Hotel, Briefcase, FileText, Settings } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaxConfigPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [catalogs, setCatalogs] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  
  // IEPS and Retenciones catalogs
  const [iepsCatalog, setIepsCatalog] = useState(null);
  const [retencionesCatalog, setRetencionesCatalog] = useState(null);
  
  // Plataformas Digitales
  const [plataformasCatalog, setPlataformasCatalog] = useState(null);
  const [platCalcOpen, setPlatCalcOpen] = useState(false);
  const [platCalcData, setPlatCalcData] = useState({
    ingreso_bruto: "",
    tipo_plataforma: "transporte_personas",
    tiene_rfc: true
  });
  const [platCalcResult, setPlatCalcResult] = useState(null);
  const [calculatingPlat, setCalculatingPlat] = useState(false);
  
  // Juegos y Sorteos
  const [juegosCatalog, setJuegosCatalog] = useState(null);
  const [juegosCalcOpen, setJuegosCalcOpen] = useState(false);
  const [juegosCalcData, setJuegosCalcData] = useState({
    monto_apostado: "",
    tipo_juego: "apuestas_deportivas",
    usar_tasa_2026: false
  });
  const [juegosCalcResult, setJuegosCalcResult] = useState(null);
  const [calculatingJuegos, setCalculatingJuegos] = useState(false);
  
  // Impuestos Locales (ISH, ISN, Cedular)
  const [localesCatalog, setLocalesCatalog] = useState(null);
  const [ishCalcOpen, setIshCalcOpen] = useState(false);
  const [ishCalcData, setIshCalcData] = useState({
    monto_hospedaje: "",
    estado: "CDMX",
    es_plataforma: false
  });
  const [ishCalcResult, setIshCalcResult] = useState(null);
  const [calculatingIsh, setCalculatingIsh] = useState(false);
  const [companyTaxConfig, setCompanyTaxConfig] = useState({
    estado: "CDMX",
    ish_enabled: false,
    isn_enabled: false,
    cedular_enabled: false,
    cedular_tipo: "profesionales"
  });
  
  // Retenciones calculator
  const [retCalcOpen, setRetCalcOpen] = useState(false);
  const [retCalcData, setRetCalcData] = useState({
    subtotal: "",
    emisor_regimen: "626",
    receptor_regimen: "601",
    tipo_operacion: "servicios_profesionales"
  });
  const [retCalcResult, setRetCalcResult] = useState(null);
  const [calculatingRet, setCalculatingRet] = useState(false);
  
  // Form states
  const [newOverride, setNewOverride] = useState({
    clave_producto: "",
    objeto_imp: "02",
    tipo_factor: "Tasa",
    tasa_o_cuota: "0.160000",
    notes: ""
  });
  
  // Calculator states
  const [calcSubtotal, setCalcSubtotal] = useState("");
  const [calcProduct, setCalcProduct] = useState("");
  const [calcResult, setCalcResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      // Use Promise.allSettled to handle individual failures gracefully
      const results = await Promise.allSettled([
        axios.get(`${API}/tax/config`, { headers }),
        axios.get(`${API}/tax/catalogs`, { headers }),
        axios.get(`${API}/tax/product-overrides`, { headers }),
        axios.get(`${API}/tax/ieps/catalogs`, { headers }),
        axios.get(`${API}/tax/retenciones/catalogs`, { headers }),
        axios.get(`${API}/tax/plataformas/catalogs`, { headers }),
        axios.get(`${API}/tax/juegos/catalogs`, { headers }),
        axios.get(`${API}/tax/locales/catalogs`, { headers }),
        axios.get(`${API}/tax/company-config`, { headers })
      ]);
      
      // Extract data from settled promises
      const [configRes, catalogsRes, overridesRes, iepsRes, retencionesRes, plataformasRes, juegosRes, localesRes, companyConfigRes] = results;
      
      if (configRes.status === 'fulfilled') setConfig(configRes.value.data);
      if (catalogsRes.status === 'fulfilled') setCatalogs(catalogsRes.value.data);
      if (overridesRes.status === 'fulfilled') setOverrides(overridesRes.value.data.overrides || []);
      if (iepsRes.status === 'fulfilled') setIepsCatalog(iepsRes.value.data);
      if (retencionesRes.status === 'fulfilled') setRetencionesCatalog(retencionesRes.value.data);
      if (plataformasRes.status === 'fulfilled') setPlataformasCatalog(plataformasRes.value.data);
      if (juegosRes.status === 'fulfilled') setJuegosCatalog(juegosRes.value.data);
      if (localesRes.status === 'fulfilled') setLocalesCatalog(localesRes.value.data);
      if (companyConfigRes.status === 'fulfilled' && companyConfigRes.value.data) {
        setCompanyTaxConfig(prev => ({ ...prev, ...companyConfigRes.value.data }));
      }
      
      // Check if any critical endpoints failed
      const criticalFailed = [configRes, catalogsRes].some(r => r.status === 'rejected');
      if (criticalFailed) {
        toast.error("Error al cargar configuración de impuestos");
      }
    } catch (error) {
      toast.error("Error al cargar configuración de impuestos");
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(`${API}/tax/config`, {
        default_iva_rate: config.default_iva_rate,
        is_frontier_zone: config.is_frontier_zone,
        frontier_zone_type: config.frontier_zone_type,
        company_postal_code: config.company_postal_code
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success("Configuración guardada");
      fetchData();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const checkPostalCode = async (cp) => {
    try {
      const token = localStorage.getItem("cia_token");
      const res = await axios.get(`${API}/tax/check-postal-code/${cp}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.is_frontier_zone) {
        toast.success(`CP ${cp} está en zona fronteriza - IVA ${res.data.percentage}%`);
        setConfig(prev => ({
          ...prev,
          is_frontier_zone: true,
          frontier_zone_type: res.data.frontier_type,
          default_iva_rate: res.data.applicable_rate
        }));
      } else {
        toast.info(`CP ${cp} no está en zona fronteriza - IVA 16%`);
        setConfig(prev => ({
          ...prev,
          is_frontier_zone: false,
          frontier_zone_type: null,
          default_iva_rate: "0.160000"
        }));
      }
    } catch (error) {
      toast.error("Error al verificar código postal");
    }
  };

  const addOverride = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(`${API}/tax/product-override`, newOverride, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Configuración de producto guardada");
      setDialogOpen(false);
      setNewOverride({
        clave_producto: "",
        objeto_imp: "02",
        tipo_factor: "Tasa",
        tasa_o_cuota: "0.160000",
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  const deleteOverride = async (clave) => {
    try {
      const token = localStorage.getItem("cia_token");
      await axios.delete(`${API}/tax/product-override/${clave}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Configuración eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const calculateTax = async () => {
    if (!calcSubtotal || !calcProduct) {
      toast.error("Ingresa subtotal y clave de producto");
      return;
    }
    
    setCalculating(true);
    try {
      const token = localStorage.getItem("cia_token");
      const res = await axios.get(`${API}/tax/calculate`, {
        params: { subtotal: calcSubtotal, clave_producto: calcProduct },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCalcResult(res.data);
    } catch (error) {
      toast.error("Error al calcular");
    } finally {
      setCalculating(false);
    }
  };

  const calculateRetenciones = async () => {
    if (!retCalcData.subtotal) {
      toast.error("Ingresa el subtotal");
      return;
    }
    
    setCalculatingRet(true);
    try {
      const token = localStorage.getItem("cia_token");
      const res = await axios.get(`${API}/tax/retenciones/calculate`, {
        params: retCalcData,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRetCalcResult(res.data);
    } catch (error) {
      toast.error("Error al calcular retenciones");
    } finally {
      setCalculatingRet(false);
    }
  };

  const calculatePlataformas = async () => {
    if (!platCalcData.ingreso_bruto) {
      toast.error("Ingresa el ingreso bruto");
      return;
    }
    
    setCalculatingPlat(true);
    try {
      const token = localStorage.getItem("cia_token");
      const res = await axios.get(`${API}/tax/plataformas/calculate`, {
        params: platCalcData,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPlatCalcResult(res.data);
    } catch (error) {
      toast.error("Error al calcular");
    } finally {
      setCalculatingPlat(false);
    }
  };

  const calculateJuegos = async () => {
    if (!juegosCalcData.monto_apostado) {
      toast.error("Ingresa el monto");
      return;
    }
    
    setCalculatingJuegos(true);
    try {
      const token = localStorage.getItem("cia_token");
      const res = await axios.get(`${API}/tax/juegos/calculate`, {
        params: juegosCalcData,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setJuegosCalcResult(res.data);
    } catch (error) {
      toast.error("Error al calcular");
    } finally {
      setCalculatingJuegos(false);
    }
  };

  const calculateIsh = async () => {
    if (!ishCalcData.monto_hospedaje) {
      toast.error("Ingresa el monto de hospedaje");
      return;
    }
    
    setCalculatingIsh(true);
    try {
      const token = localStorage.getItem("cia_token");
      const res = await axios.get(`${API}/tax/locales/ish/calculate`, {
        params: ishCalcData,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIshCalcResult(res.data);
    } catch (error) {
      toast.error("Error al calcular ISH");
    } finally {
      setCalculatingIsh(false);
    }
  };

  const saveCompanyTaxConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(`${API}/tax/company-config`, companyTaxConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configuración de impuestos locales guardada");
      fetchData();
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const getRateBadge = (rate) => {
    if (rate === "0.160000") return <Badge className="bg-blue-500">16%</Badge>;
    if (rate === "0.080000") return <Badge className="bg-green-500">8% Frontera</Badge>;
    if (rate === "0.000000") return <Badge className="bg-yellow-500 text-black">0%</Badge>;
    if (rate === "exento") return <Badge variant="outline">Exento</Badge>;
    return <Badge>{rate}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#5B9BD5]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tax-config-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configuración de Impuestos</h1>
          <p className="text-slate-600">Gestiona IVA, IEPS y Retenciones para tu empresa</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={calculatorOpen} onOpenChange={setCalculatorOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Calculator className="w-4 h-4 mr-2" />
                Calculadora IVA
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Calculadora de IVA</DialogTitle>
                <DialogDescription>Calcula el IVA según producto y ubicación</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Subtotal</Label>
                  <Input
                    type="number"
                    placeholder="1000.00"
                    value={calcSubtotal}
                    onChange={(e) => setCalcSubtotal(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Clave de Producto SAT</Label>
                  <Input
                    placeholder="80141600"
                    value={calcProduct}
                    onChange={(e) => setCalcProduct(e.target.value)}
                  />
                </div>
                <Button onClick={calculateTax} disabled={calculating} className="w-full">
                  {calculating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Calcular
                </Button>
                
                {calcResult && (
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span className="font-medium">${calcResult.subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>IVA ({calcResult.percentage ?? 'Exento'}%):</span>
                      <span className="font-medium">${calcResult.iva_amount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total:</span>
                      <span>${calcResult.total.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={retCalcOpen} onOpenChange={setRetCalcOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <ArrowDownLeft className="w-4 h-4 mr-2" />
                Calculadora Retenciones
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Calculadora de Retenciones</DialogTitle>
                <DialogDescription>Calcula ISR e IVA retenido cuando Persona Física factura a Persona Moral</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Subtotal</Label>
                  <Input
                    type="number"
                    placeholder="10000.00"
                    value={retCalcData.subtotal}
                    onChange={(e) => setRetCalcData(prev => ({ ...prev, subtotal: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-1"><User className="w-3 h-3" /> Emisor (quien factura)</Label>
                    <Select
                      value={retCalcData.emisor_regimen}
                      onValueChange={(v) => setRetCalcData(prev => ({ ...prev, emisor_regimen: v }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="626">626 - RESICO</SelectItem>
                        <SelectItem value="612">612 - Actividades Empresariales PF</SelectItem>
                        <SelectItem value="606">606 - Arrendamiento</SelectItem>
                        <SelectItem value="608">608 - Demás ingresos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="flex items-center gap-1"><Building2 className="w-3 h-3" /> Receptor (quien paga)</Label>
                    <Select
                      value={retCalcData.receptor_regimen}
                      onValueChange={(v) => setRetCalcData(prev => ({ ...prev, receptor_regimen: v }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="601">601 - General de Ley PM</SelectItem>
                        <SelectItem value="603">603 - Fines no Lucrativos</SelectItem>
                        <SelectItem value="620">620 - Cooperativas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Tipo de Operación</Label>
                  <Select
                    value={retCalcData.tipo_operacion}
                    onValueChange={(v) => setRetCalcData(prev => ({ ...prev, tipo_operacion: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="servicios_profesionales">Servicios Profesionales / Honorarios</SelectItem>
                      <SelectItem value="arrendamiento">Arrendamiento de Inmuebles</SelectItem>
                      <SelectItem value="transporte_terrestre">Transporte Terrestre</SelectItem>
                      <SelectItem value="comisiones">Comisiones</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={calculateRetenciones} disabled={calculatingRet} className="w-full">
                  {calculatingRet ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Calcular
                </Button>
                
                {retCalcResult && (
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                    {!retCalcResult.applies_retention ? (
                      <div className="text-center text-slate-600 py-4">
                        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                        <p>{retCalcResult.reason}</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span>Subtotal:</span>
                          <span className="font-medium">${retCalcResult.subtotal.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-green-600">
                          <span>+ IVA Trasladado (16%):</span>
                          <span>${retCalcResult.total_impuestos_trasladados.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-red-600">
                          <span>- ISR Retenido ({retCalcResult.is_resico ? '1.25%' : '10%'}):</span>
                          <span>-${retCalcResult.impuestos_retenidos.isr.importe.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-red-600">
                          <span>- IVA Retenido (10.67%):</span>
                          <span>-${retCalcResult.impuestos_retenidos.iva.importe.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                          <span>Total a Pagar:</span>
                          <span>${retCalcResult.total.toLocaleString()}</span>
                        </div>
                        {retCalcResult.is_resico && (
                          <p className="text-xs text-blue-600 mt-2">* Tasa ISR reducida por régimen RESICO</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="iva" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="iva">IVA</TabsTrigger>
          <TabsTrigger value="ieps">IEPS</TabsTrigger>
          <TabsTrigger value="retenciones">Retenciones</TabsTrigger>
          <TabsTrigger value="plataformas">Plataformas</TabsTrigger>
          <TabsTrigger value="juegos">Juegos</TabsTrigger>
          <TabsTrigger value="locales">Locales</TabsTrigger>
        </TabsList>
        
        {/* IVA TAB */}
        <TabsContent value="iva" className="space-y-6 mt-6">
          {/* Info card */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-4">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Tasas de IVA en México:</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700">
                    <li><strong>16%</strong> - Tasa general en todo México</li>
                    <li><strong>8%</strong> - Zona fronteriza norte y sur</li>
                    <li><strong>0%</strong> - Alimentos básicos, medicinas, exportaciones</li>
                    <li><strong>Exento</strong> - Servicios médicos, educación, vivienda</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* General config */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Configuración General
              </CardTitle>
              <CardDescription>Tasa de IVA por defecto basada en tu ubicación</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Código Postal de tu Empresa</Label>
                  <div className="flex gap-2">
                    <Input
                      value={config?.company_postal_code || ""}
                      onChange={(e) => setConfig(prev => ({ ...prev, company_postal_code: e.target.value }))}
                      placeholder="06600"
                      maxLength={5}
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => checkPostalCode(config?.company_postal_code)}
                      disabled={!config?.company_postal_code || config?.company_postal_code.length < 5}
                    >
                      Verificar
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Tasa de IVA por Defecto</Label>
                  <Select
                    value={config?.default_iva_rate || "0.160000"}
                    onValueChange={(value) => setConfig(prev => ({ ...prev, default_iva_rate: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.160000">16% - Tasa General</SelectItem>
                      <SelectItem value="0.080000">8% - Zona Fronteriza</SelectItem>
                      <SelectItem value="0.000000">0% - Tasa Cero</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {config?.is_frontier_zone ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  )}
                  <div>
                    <p className="font-medium">
                      {config?.is_frontier_zone ? "Zona Fronteriza Detectada" : "Zona General"}
                    </p>
                    <p className="text-sm text-slate-500">
                      {config?.is_frontier_zone 
                        ? `Aplica IVA 8% (Frontera ${config?.frontier_zone_type})`
                        : "Aplica IVA 16% por defecto"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="frontier-switch">Forzar zona fronteriza</Label>
                  <Switch
                    id="frontier-switch"
                    checked={config?.is_frontier_zone || false}
                    onCheckedChange={(checked) => setConfig(prev => ({ 
                      ...prev, 
                      is_frontier_zone: checked,
                      default_iva_rate: checked ? "0.080000" : "0.160000"
                    }))}
                  />
                </div>
              </div>
              
              <Button onClick={saveConfig} disabled={saving} className="bg-[#5B9BD5]">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Guardar Configuración
              </Button>
            </CardContent>
          </Card>

          {/* Product overrides */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Configuración por Producto
                  </CardTitle>
                  <CardDescription>Define tasas especiales para productos específicos</CardDescription>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Configurar IVA para Producto</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Clave de Producto SAT</Label>
                        <SatProductSearch
                          value={newOverride.clave_producto}
                          onChange={(clave) => setNewOverride(prev => ({ ...prev, clave_producto: clave }))}
                          onDescriptionSelect={(desc) => setNewOverride(prev => ({ ...prev, descripcion: desc }))}
                          placeholder="Buscar clave o descripción..."
                        />
                      </div>
                      <div>
                        <Label>Tipo de Factor</Label>
                        <Select
                          value={newOverride.tipo_factor}
                          onValueChange={(value) => setNewOverride(prev => ({ ...prev, tipo_factor: value }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Tasa">Tasa</SelectItem>
                            <SelectItem value="Exento">Exento</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {newOverride.tipo_factor !== "Exento" && (
                        <div>
                          <Label>Tasa</Label>
                          <Select
                            value={newOverride.tasa_o_cuota}
                            onValueChange={(value) => setNewOverride(prev => ({ ...prev, tasa_o_cuota: value }))}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0.160000">16%</SelectItem>
                              <SelectItem value="0.080000">8%</SelectItem>
                              <SelectItem value="0.000000">0%</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div>
                        <Label>Notas</Label>
                        <Input
                          placeholder="Ej: Alimentos sin procesar"
                          value={newOverride.notes}
                          onChange={(e) => setNewOverride(prev => ({ ...prev, notes: e.target.value }))}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button onClick={addOverride}>Guardar</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {overrides.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No tienes configuraciones especiales</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Clave</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Tasa</TableHead>
                      <TableHead>Notas</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {overrides.map((o) => (
                      <TableRow key={o.clave_producto}>
                        <TableCell className="font-mono">{o.clave_producto}</TableCell>
                        <TableCell>{o.tipo_factor}</TableCell>
                        <TableCell>{getRateBadge(o.tasa_o_cuota || "exento")}</TableCell>
                        <TableCell className="text-slate-500 text-sm">{o.notes || "-"}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => deleteOverride(o.clave_producto)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* IEPS TAB */}
        <TabsContent value="ieps" className="space-y-6 mt-6">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <div className="flex gap-3">
                <Fuel className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">IEPS (Impuesto Especial sobre Producción y Servicios)</p>
                  <p className="text-amber-700">
                    Aplica a combustibles, alcohol, tabaco, bebidas azucaradas y alimentos de alto contenido calórico.
                    Se calcula por <strong>cuota fija</strong> (pesos por unidad) o por <strong>tasa</strong> (% del precio).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Combustibles */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Fuel className="w-5 h-5" />
                  Combustibles (Cuota por litro)
                </CardTitle>
                <CardDescription>Cuotas actualizadas 2025</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Producto</TableHead>
                      <TableHead className="text-right">Cuota/Litro</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {iepsCatalog?.ieps_cuotas_2025 && Object.entries(iepsCatalog.ieps_cuotas_2025)
                      .filter(([k, v]) => k.startsWith("1510"))
                      .map(([clave, info]) => (
                        <TableRow key={clave}>
                          <TableCell>
                            <span className="font-mono text-xs text-slate-500">{clave}</span>
                            <br />{info.name}
                          </TableCell>
                          <TableCell className="text-right font-medium">${info.cuota?.toFixed(4)}</TableCell>
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Bebidas Alcohólicas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Wine className="w-5 h-5" />
                  Bebidas Alcohólicas (Tasa %)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Tasa</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {iepsCatalog?.ieps_cuotas_2025 && Object.entries(iepsCatalog.ieps_cuotas_2025)
                      .filter(([k, v]) => k.startsWith("502021") && v.tasa)
                      .map(([clave, info]) => (
                        <TableRow key={clave}>
                          <TableCell>
                            <span className="font-mono text-xs text-slate-500">{clave}</span>
                            <br />{info.name}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline">{(info.tasa * 100).toFixed(1)}%</Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Otros productos */}
          <Card>
            <CardHeader>
              <CardTitle>Otros Productos con IEPS</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-2">Bebidas Saborizadas</h4>
                  <p className="text-2xl font-bold text-amber-600">$1.6451/L</p>
                  <p className="text-xs text-slate-500">Refrescos, jugos con azúcar</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-2">Tabaco</h4>
                  <p className="text-2xl font-bold text-amber-600">$0.6445/pieza</p>
                  <p className="text-xs text-slate-500">Cigarros, puros</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-2">Alimentos Alto Calórico</h4>
                  <p className="text-2xl font-bold text-amber-600">8%</p>
                  <p className="text-xs text-slate-500">Botanas, dulces, chocolates</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* RETENCIONES TAB */}
        <TabsContent value="retenciones" className="space-y-6 mt-6">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-4">
              <div className="flex gap-3">
                <ArrowDownLeft className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <p className="font-medium mb-1">Retenciones de ISR e IVA</p>
                  <p className="text-red-700">
                    Cuando una <strong>Persona Física</strong> factura a una <strong>Persona Moral</strong>, 
                    la persona moral debe retener y enterar parte del ISR e IVA al SAT.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Retención ISR */}
            <Card>
              <CardHeader>
                <CardTitle>Retención ISR</CardTitle>
                <CardDescription>Impuesto Sobre la Renta</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo de Operación</TableHead>
                      <TableHead className="text-right">Tasa</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>Honorarios Profesionales</TableCell>
                      <TableCell className="text-right"><Badge className="bg-red-500">10%</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        RESICO (Simplificado de Confianza)
                        <span className="text-xs text-green-600 block">Tasa preferencial</span>
                      </TableCell>
                      <TableCell className="text-right"><Badge className="bg-green-500">1.25%</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Arrendamiento</TableCell>
                      <TableCell className="text-right"><Badge className="bg-red-500">10%</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Comisiones</TableCell>
                      <TableCell className="text-right"><Badge className="bg-red-500">10%</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Desperdicios Industriales</TableCell>
                      <TableCell className="text-right"><Badge className="bg-red-500">20%</Badge></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Retención IVA */}
            <Card>
              <CardHeader>
                <CardTitle>Retención IVA</CardTitle>
                <CardDescription>Impuesto al Valor Agregado</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo de Operación</TableHead>
                      <TableHead className="text-right">Tasa</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        Servicios Profesionales
                        <span className="text-xs text-slate-500 block">2/3 del IVA trasladado</span>
                      </TableCell>
                      <TableCell className="text-right"><Badge className="bg-red-500">10.6666%</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Arrendamiento</TableCell>
                      <TableCell className="text-right"><Badge className="bg-red-500">10.6666%</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Transporte Terrestre</TableCell>
                      <TableCell className="text-right"><Badge className="bg-orange-500">4%</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Desperdicios Industriales</TableCell>
                      <TableCell className="text-right"><Badge className="bg-red-500">16%</Badge></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Ejemplo visual */}
          <Card>
            <CardHeader>
              <CardTitle>Ejemplo: Cómo aparecen las Retenciones en el PDF</CardTitle>
              <CardDescription>Servicios profesionales de $10,000 MXN</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-white border rounded-lg p-6 max-w-md mx-auto shadow-sm">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span className="font-medium">$10,000.00</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>IVA Trasladado (16%):</span>
                    <span>$1,600.00</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>(-) ISR Retenido (10%):</span>
                    <span>-$1,000.00</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>(-) IVA Retenido (10.67%):</span>
                    <span>-$1,066.67</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                    <span>Total a Pagar:</span>
                    <span>$9,533.33</span>
                  </div>
                </div>
              </div>
              <p className="text-center text-xs text-slate-500 mt-4">
                Las retenciones se restan del total porque el receptor (PM) las pagará directamente al SAT
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* PLATAFORMAS DIGITALES TAB */}
        <TabsContent value="plataformas" className="space-y-6 mt-6">
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="pt-4">
              <div className="flex gap-3">
                <Smartphone className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-purple-800">
                  <p className="font-medium mb-1">Retenciones por Plataformas Digitales</p>
                  <p className="text-purple-700">
                    Cuando trabajas con plataformas como Uber, Rappi, DiDi, Airbnb, Amazon, etc., 
                    estas retienen parte de tu ISR e IVA y lo pagan directamente al SAT.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Calculadora de Plataformas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Calculadora de Retenciones
              </CardTitle>
              <CardDescription>Calcula cuánto te retendrá la plataforma</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Ingreso Bruto</Label>
                  <Input
                    type="number"
                    placeholder="5000.00"
                    value={platCalcData.ingreso_bruto}
                    onChange={(e) => setPlatCalcData(prev => ({ ...prev, ingreso_bruto: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Tipo de Plataforma</Label>
                  <Select
                    value={platCalcData.tipo_plataforma}
                    onValueChange={(v) => setPlatCalcData(prev => ({ ...prev, tipo_plataforma: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transporte_personas">Uber / DiDi / Cabify</SelectItem>
                      <SelectItem value="entrega_alimentos">Rappi / Uber Eats / DiDi Food</SelectItem>
                      <SelectItem value="hospedaje">Airbnb / Booking</SelectItem>
                      <SelectItem value="intermediacion">Amazon / Mercado Libre</SelectItem>
                      <SelectItem value="contenido_digital">Netflix / Spotify (solo IVA)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button onClick={calculatePlataformas} disabled={calculatingPlat} className="w-full">
                    {calculatingPlat ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Calcular
                  </Button>
                </div>
              </div>
              
              {platCalcResult && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                  <h4 className="font-medium">{platCalcResult.plataforma_name}</h4>
                  {!platCalcResult.aplica_retencion ? (
                    <div className="text-slate-600">
                      <p>{platCalcResult.message}</p>
                      <div className="flex justify-between mt-2">
                        <span>IVA Trasladado:</span>
                        <span className="text-green-600">+${platCalcResult.iva_trasladado?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-2 mt-2">
                        <span>Total:</span>
                        <span>${platCalcResult.total_a_pagar?.toLocaleString()}</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span>Ingreso Bruto:</span>
                        <span>${platCalcResult.ingreso_bruto?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-green-600">
                        <span>+ IVA Trasladado (16%):</span>
                        <span>+${platCalcResult.impuestos?.iva_trasladado?.importe?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>- ISR Retenido ({platCalcResult.impuestos?.isr_retenido?.porcentaje}%):</span>
                        <span>-${platCalcResult.impuestos?.isr_retenido?.importe?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>- IVA Retenido ({platCalcResult.impuestos?.iva_retenido?.porcentaje}%):</span>
                        <span>-${platCalcResult.impuestos?.iva_retenido?.importe?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                        <span>Recibirás:</span>
                        <span className="text-green-600">${platCalcResult.neto_a_recibir?.toLocaleString()}</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Catálogo de plataformas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Car className="w-5 h-5 text-blue-500" />
                  Transporte
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-2">Uber, DiDi, Cabify, InDriver</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>ISR:</span>
                    <Badge className="bg-red-500">2.1%</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>IVA Retenido:</span>
                    <Badge className="bg-red-500">8%</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ShoppingBag className="w-5 h-5 text-orange-500" />
                  Delivery
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-2">Rappi, Uber Eats, DiDi Food</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>ISR:</span>
                    <Badge className="bg-red-500">2.1%</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>IVA Retenido:</span>
                    <Badge className="bg-red-500">8%</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Home className="w-5 h-5 text-pink-500" />
                  Hospedaje
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-2">Airbnb, Booking, Vrbo</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>ISR:</span>
                    <Badge className="bg-red-500">4%</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>IVA Retenido:</span>
                    <Badge className="bg-red-500">8%</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Nota importante */}
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">Importante sobre plataformas:</p>
                  <ul className="list-disc list-inside space-y-1 text-yellow-700">
                    <li>Si NO tienes tu RFC registrado en la plataforma, te retienen <strong>20% ISR + 100% IVA</strong></li>
                    <li>Debes declarar estos ingresos en tu declaración anual</li>
                    <li>Las retenciones son <strong>pagos provisionales</strong>, no definitivos</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* JUEGOS Y SORTEOS TAB */}
        <TabsContent value="juegos" className="space-y-6 mt-6">
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="pt-4">
              <div className="flex gap-3">
                <Gamepad2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-800">
                  <p className="font-medium mb-1">IEPS sobre Juegos con Apuestas y Sorteos</p>
                  <p className="text-emerald-700">
                    Aplica a casinos, apuestas deportivas, loterías, rifas, bingos y juegos en línea.
                    La tasa actual es <strong>30%</strong> pero aumentará a <strong>50%</strong> a partir de 2026.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Calculadora de IEPS Juegos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Calculadora IEPS Juegos
              </CardTitle>
              <CardDescription>Calcula el IEPS sobre apuestas y sorteos</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Monto Apostado/Jugado</Label>
                  <Input
                    type="number"
                    placeholder="1000.00"
                    value={juegosCalcData.monto_apostado}
                    onChange={(e) => setJuegosCalcData(prev => ({ ...prev, monto_apostado: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Tipo de Juego</Label>
                  <Select
                    value={juegosCalcData.tipo_juego}
                    onValueChange={(v) => setJuegosCalcData(prev => ({ ...prev, tipo_juego: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="casino">Casino</SelectItem>
                      <SelectItem value="apuestas_deportivas">Apuestas Deportivas</SelectItem>
                      <SelectItem value="apuestas_online">Apuestas en Línea</SelectItem>
                      <SelectItem value="loteria">Lotería</SelectItem>
                      <SelectItem value="rifas">Rifas y Sorteos</SelectItem>
                      <SelectItem value="bingo">Bingo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={juegosCalcData.usar_tasa_2026}
                    onCheckedChange={(v) => setJuegosCalcData(prev => ({ ...prev, usar_tasa_2026: v }))}
                  />
                  <Label className="text-sm">Usar tasa 2026 (50%)</Label>
                </div>
                <div className="flex items-end">
                  <Button onClick={calculateJuegos} disabled={calculatingJuegos} className="w-full">
                    {calculatingJuegos ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Calcular
                  </Button>
                </div>
              </div>
              
              {juegosCalcResult && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                  <h4 className="font-medium">{juegosCalcResult.nombre_juego}</h4>
                  <p className="text-xs text-slate-500">Vigencia: {juegosCalcResult.vigencia}</p>
                  <div className="flex justify-between">
                    <span>Base (monto apostado):</span>
                    <span>${juegosCalcResult.monto_apostado?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-amber-600">
                    <span>IEPS ({juegosCalcResult.porcentaje_ieps}%):</span>
                    <span>${juegosCalcResult.ieps_amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                    <span>Total con IEPS:</span>
                    <span>${juegosCalcResult.total_con_ieps?.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">{juegosCalcResult.nota}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tabla de tasas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                Tasas IEPS por Tipo de Juego
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo de Juego</TableHead>
                    <TableHead className="text-center">Tasa 2024-2025</TableHead>
                    <TableHead className="text-center">Tasa 2026+</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>Casinos y Salas de Juego</TableCell>
                    <TableCell className="text-center"><Badge className="bg-amber-500">30%</Badge></TableCell>
                    <TableCell className="text-center"><Badge className="bg-red-500">50%</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Apuestas Deportivas</TableCell>
                    <TableCell className="text-center"><Badge className="bg-amber-500">30%</Badge></TableCell>
                    <TableCell className="text-center"><Badge className="bg-red-500">50%</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Apuestas en Línea</TableCell>
                    <TableCell className="text-center"><Badge className="bg-amber-500">30%</Badge></TableCell>
                    <TableCell className="text-center"><Badge className="bg-red-500">50%</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Loterías</TableCell>
                    <TableCell className="text-center"><Badge className="bg-amber-500">30%</Badge></TableCell>
                    <TableCell className="text-center"><Badge className="bg-red-500">50%</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Rifas y Sorteos</TableCell>
                    <TableCell className="text-center"><Badge className="bg-amber-500">30%</Badge></TableCell>
                    <TableCell className="text-center"><Badge className="bg-red-500">50%</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Máquinas Tragamonedas</TableCell>
                    <TableCell className="text-center"><Badge className="bg-amber-500">30%</Badge></TableCell>
                    <TableCell className="text-center"><Badge className="bg-red-500">50%</Badge></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Impuesto sobre premios */}
          <Card>
            <CardHeader>
              <CardTitle>Impuesto sobre Premios Ganados</CardTitle>
              <CardDescription>ISR federal + impuesto estatal sobre premios de juegos</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="font-medium">ISR Federal sobre Premios:</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between p-2 bg-green-50 rounded">
                      <span>Si impuesto estatal ≤ 6%:</span>
                      <Badge className="bg-green-500">1%</Badge>
                    </div>
                    <div className="flex justify-between p-2 bg-red-50 rounded">
                      <span>Si impuesto estatal &gt; 6%:</span>
                      <Badge className="bg-red-500">21%</Badge>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <h4 className="font-medium">Impuesto Estatal (ejemplos):</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>CDMX, Nuevo León, Jalisco:</span>
                      <span className="font-medium">6%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Estado de México:</span>
                      <span className="font-medium">6%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Otros estados:</span>
                      <span className="font-medium">3% - 6%</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Ejemplo:</strong> Si ganas $100,000 en CDMX:
                  <br />• Impuesto estatal (6%): $6,000
                  <br />• ISR federal (1%): $1,000
                  <br />• <strong>Premio neto: $93,000</strong>
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* IMPUESTOS LOCALES TAB */}
        <TabsContent value="locales" className="space-y-6 mt-6">
          <Card className="border-teal-200 bg-teal-50">
            <CardContent className="pt-4">
              <div className="flex gap-3">
                <Hotel className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-teal-800">
                  <p className="font-medium mb-1">Impuestos Locales y Estatales</p>
                  <p className="text-teal-700">
                    México tiene impuestos locales que varían por estado: <strong>ISH</strong> (Hospedaje), 
                    <strong> ISN</strong> (Nómina), e <strong>Impuesto Cedular</strong> (Profesionales/Arrendamiento).
                    Configura los que apliquen a tu negocio.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ISH - Impuesto Sobre Hospedaje */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hotel className="w-5 h-5 text-teal-600" />
                ISH - Impuesto Sobre Hospedaje
              </CardTitle>
              <CardDescription>
                Aplica a servicios de alojamiento: hoteles, Airbnb, moteles, hostales.
                La tasa varía por estado (2% - 6%).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* ISH Calculator */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Calculadora ISH
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Monto Hospedaje</Label>
                    <Input
                      type="number"
                      placeholder="1500.00"
                      value={ishCalcData.monto_hospedaje}
                      onChange={(e) => setIshCalcData(prev => ({ ...prev, monto_hospedaje: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Estado</Label>
                    <Select
                      value={ishCalcData.estado}
                      onValueChange={(v) => setIshCalcData(prev => ({ ...prev, estado: v }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {localesCatalog?.estados?.map((est) => (
                          <SelectItem key={est} value={est}>{localesCatalog?.ish?.[est]?.name || est}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Switch
                      checked={ishCalcData.es_plataforma}
                      onCheckedChange={(v) => setIshCalcData(prev => ({ ...prev, es_plataforma: v }))}
                    />
                    <Label className="text-sm">Plataforma (Airbnb)</Label>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={calculateIsh} disabled={calculatingIsh} className="w-full bg-teal-600 hover:bg-teal-700">
                      {calculatingIsh ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Calcular
                    </Button>
                  </div>
                </div>
                
                {ishCalcResult && (
                  <div className="bg-white rounded-lg p-4 space-y-2 text-sm border">
                    <h5 className="font-medium">{ishCalcResult.estado_name || ishCalcResult.estado_nombre}</h5>
                    <div className="flex justify-between">
                      <span>Hospedaje:</span>
                      <span>${ishCalcResult.monto_hospedaje?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-teal-600">
                      <span>ISH ({ishCalcResult.porcentaje_ish}%):</span>
                      <span>+${ishCalcResult.ish_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                      <span>Total con ISH:</span>
                      <span>${(ishCalcResult.subtotal_con_ish || ishCalcResult.total_con_ish)?.toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">{ishCalcResult.nota}</p>
                  </div>
                )}
              </div>

              {/* ISH Rates Table */}
              <div>
                <h4 className="font-medium mb-3">Tasas ISH por Estado</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {localesCatalog?.ish && Object.entries(localesCatalog.ish).map(([code, info]) => (
                    <div key={code} className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-500 font-mono">{code}</div>
                      <div className="font-medium text-sm">{info.name}</div>
                      <div className="flex gap-2 mt-1">
                        <Badge className="bg-teal-500 text-xs">{(info.tasa * 100).toFixed(1)}%</Badge>
                        {info.tasa_plataformas !== info.tasa && (
                          <Badge variant="outline" className="text-xs">{(info.tasa_plataformas * 100).toFixed(1)}% Plat.</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ISN - Impuesto Sobre Nómina */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-orange-600" />
                ISN - Impuesto Sobre Nómina
              </CardTitle>
              <CardDescription>
                Impuesto estatal sobre pagos de nómina. Lo paga el patrón sobre los sueldos.
                Tasa varía por estado (2% - 4.25%).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {localesCatalog?.isn && Object.entries(localesCatalog.isn).map(([code, info]) => (
                  <div key={code} className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500 font-mono">{code}</div>
                    <div className="font-medium text-sm">{info.name}</div>
                    <Badge className="bg-orange-500 mt-1">{(info.tasa * 100).toFixed(2)}%</Badge>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800">
                  <strong>Nota:</strong> El ISN se calcula sobre el total de pagos de nómina 
                  (sueldos, salarios, gratificaciones, prestaciones) y se declara mensualmente.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Impuesto Cedular */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Impuesto Cedular
              </CardTitle>
              <CardDescription>
                Solo aplica en algunos estados para profesionistas y arrendadores.
                Es un impuesto local sobre ingresos específicos.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {localesCatalog?.cedular && Object.entries(localesCatalog.cedular).map(([code, info]) => (
                    <div key={code} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-slate-500 font-mono bg-slate-200 px-2 py-0.5 rounded">{code}</span>
                        <span className="font-medium">{info.name}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex justify-between">
                          <span>Profesionales:</span>
                          <Badge className="bg-purple-500">{(info.tasa_profesionales * 100).toFixed(0)}%</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>Arrendamiento:</span>
                          <Badge className="bg-purple-500">{(info.tasa_arrendamiento * 100).toFixed(0)}%</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-800">
                    <strong>Importante:</strong> El impuesto cedular es adicional al ISR federal.
                    Verifica si tu estado lo aplica antes de configurarlo.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Tax Config */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configuración de Impuestos Locales para tu Empresa
              </CardTitle>
              <CardDescription>
                Activa los impuestos locales que apliquen a tu operación
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Estado donde operas</Label>
                  <Select
                    value={companyTaxConfig.estado}
                    onValueChange={(v) => setCompanyTaxConfig(prev => ({ ...prev, estado: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {localesCatalog?.estados?.map((est) => (
                        <SelectItem key={est} value={est}>{localesCatalog?.ish?.[est]?.name || est}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="flex items-center gap-3">
                    <Hotel className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="font-medium">ISH - Impuesto Sobre Hospedaje</p>
                      <p className="text-sm text-slate-500">Activa si ofreces servicios de alojamiento</p>
                    </div>
                  </div>
                  <Switch
                    checked={companyTaxConfig.ish_enabled}
                    onCheckedChange={(v) => setCompanyTaxConfig(prev => ({ ...prev, ish_enabled: v }))}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="font-medium">ISN - Impuesto Sobre Nómina</p>
                      <p className="text-sm text-slate-500">Activa si tienes empleados (solo referencia)</p>
                    </div>
                  </div>
                  <Switch
                    checked={companyTaxConfig.isn_enabled}
                    onCheckedChange={(v) => setCompanyTaxConfig(prev => ({ ...prev, isn_enabled: v }))}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-medium">Impuesto Cedular</p>
                      <p className="text-sm text-slate-500">Solo en estados que lo apliquen</p>
                    </div>
                  </div>
                  <Switch
                    checked={companyTaxConfig.cedular_enabled}
                    onCheckedChange={(v) => setCompanyTaxConfig(prev => ({ ...prev, cedular_enabled: v }))}
                  />
                </div>
              </div>

              <Button onClick={saveCompanyTaxConfig} disabled={saving} className="bg-[#5B9BD5]">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Guardar Configuración de Impuestos Locales
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TaxConfigPage;
